import {$Values} from 'utility-types'

/** @jsx jsx */
import React, {ReactNode, useContext, useRef} from 'react'
import {jsx, css} from '@emotion/core'
import styled from '@emotion/styled'
import {SerializedStyles} from '@emotion/utils'
import {Transition} from 'react-transition-group'

import {scrollTo} from '@daedalus/core/src/utils/dom'
import useWindowSize from '@daedalus/core/src/utils/dom/hooks/useWindowSize'
import {hexToRGBStr} from '@daedalus/core/src/utils/string'

import {ThemeType} from 'types/Theme'
import Portal from '../../../designSystem/helpers/Portal'
import {useDeviceLayout} from '../../../../context/deviceLayout'
import useBodyScrollLock from './useBodyScrollLock'
export {BODY_SCROLL_LOCK_IGNORE} from './useBodyScrollLock'
import transitions from '../../../../utils/transitions'

export type AnimationType = $Values<typeof ANIMATION_TYPES>

type StatusType = $Values<typeof STATUSES>

type PropsType = {
  /** The style of animation for opening and closing */
  animationType: AnimationType
  /** The body of the overlay */
  children: ReactNode
  /** Whether the overlay should call the onClose callback when the Esc key is pressed */
  closeOnEsc: boolean
  /** The styles for the overlay container */
  containerStyles?: Record<string, string | number>
  /** The styles for the overlay content */
  customStyles?: SerializedStyles
  /** Whether to add a shade to the background */
  shadeBackground?: boolean
  /** Whether the overlay is open */
  isOpen: boolean
  /** Whether the overlay background click is enabled */
  isBackgroundClickEnabled?: boolean
  /** The callback for closing the overlay. Optionally pass in the element that was selected to close. */
  onClose?: (e?: React.SyntheticEvent, element?: string) => void
  /** Enable body scroll lock  */
  enableBodyScrollLock?: boolean
  /**
   * Enable content background hack.
   * On iOS the viewport height lags after the navbar hiding on scroll, this means the content behind the overlay is shown while the navbar animates out
   * This is a bandaid solution, putting a white background under the overlay content that extends under the nav bar
   *
   * It's advisable to enable this for fullscreen overlays with a busy page underneath
   */
  enableContentBackgroundHack?: boolean
  /** Enable touchmove events when body is locked. See docs: https://www.npmjs.com/package/body-scroll-lock#allowtouchmove */
  allowTouchMove?: boolean
  /** How long the animation will last in milliseconds */
  transitionDuration: number
  /** Will pre-render the content of the overlay but keep it hidden */
  preRenderContent: boolean
}

type StateType = {
  status: StatusType
}

// @todo create a isEscapeKey in utils
const KEYCODE_ESC = 27
const KEYSTRING_ESC = 'Escape'
const KEYSTRING_SHORT_ESC = 'Esc'

const STATUSES = {
  INITIAL: 'INITIAL',
  RENDERED: 'RENDERED',
  VISIBLE: 'VISIBLE',
  HIDDEN: 'HIDDEN'
}

export const ANIMATION_TYPES = {
  SLIDE_LEFT: 'slideLeft',
  SLIDE_RIGHT: 'slideRight',
  SLIDE_UP: 'slideUp',
  SLIDE_DOWN: 'slideDown',
  FADE_IN_OUT: 'overlayFade'
}

export const TRANSITION_DURATION = 300 // ms
const SCROLL_TOP_DURATION = 250 // ms

const withPropsContainerStyles = props => {
  return css`
    ${props.containerStyles};
  `
}

const OverlayContainer = styled('div')`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: fixed;
  z-index: 100;
  ${withPropsContainerStyles};
`

const contentBaseStyles = ({theme}) => css`
  background: ${theme.colors.overlayWrapperBg};
  height: 100%;
  position: relative;
  overflow: auto;
`

const withContentShadow = ({
  theme,
  shadeBackground
}: {
  theme: ThemeType
  shadeBackground: PropsType['shadeBackground']
}) =>
  !shadeBackground &&
  css`
    box-shadow: ${theme.shadows.action};
  `

const OverlayContent = styled('div')<{
  shadeBackground: PropsType['shadeBackground']
}>(contentBaseStyles, withContentShadow)

const baseOverlayStyles = () => css`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  position: fixed;
`

const withOverlayBackground = ({theme, shadeBackground}) =>
  shadeBackground &&
  css`
    background: rgba(${hexToRGBStr(theme.colors.overlayBg)}, 0.6);
  `

const OverlayBackground = styled('div')(
  baseOverlayStyles,
  withOverlayBackground
)

const ContentBackgroundHack = styled('div')(
  ({theme}) =>
    css`
      position: absolute;
      width: 100%;
      height: 100vh; /* extends under the iOS navbar */
      background-color: ${theme.colors.defaultBg};
    `
)

export interface OverlayContextValue {
  scrollToTop: () => void
}

export const OverlayContext = React.createContext<
  OverlayContextValue | undefined
>(undefined)

export const useOverlayHandlers = () => {
  const context = useContext(OverlayContext)
  if (context === undefined) {
    throw new Error('useOverlayHandlers must be within OverlayContext Provider')
  }

  return context
}

const FullHeightContainer = ({
  children,
  containerStyles
}: {
  children: ReactNode
  containerStyles: PropsType['containerStyles']
}) => {
  // Use the true viewport-height since CSS 100% / 100vh etc. break on on iOS Chrome
  // There is a layout bug on iOS Chrome when opening a tab by clicking a link that causes the viewport extend under the navbar (interacting with the URL bar will fix the layout)
  // This bug does not show up on Browserstack. Test this flow with search overlays on a real device if any changes are made!
  const {isMobile} = useDeviceLayout()
  const {height} = useWindowSize()
  const style = {height: `${height}px`}

  return (
    <OverlayContainer
      containerStyles={containerStyles}
      {...(isMobile ? {style} : {})}
    >
      {children}
    </OverlayContainer>
  )
}

const Content = ({
  children,
  className,
  shadeBackground,
  enableBodyScrollLock,
  allowTouchMove
}: {
  children: ReactNode
  className?: string // Pass through emotion styling
  shadeBackground: PropsType['shadeBackground']
  enableBodyScrollLock: PropsType['enableBodyScrollLock']
  allowTouchMove: PropsType['allowTouchMove']
}) => {
  const ref = useRef<HTMLDivElement>()

  useBodyScrollLock({ref, isEnabled: enableBodyScrollLock, allowTouchMove})

  const scrollToTop = (duration = SCROLL_TOP_DURATION) => {
    if (ref.current) scrollTo(ref.current, 0, duration)
  }

  const contextValue: OverlayContextValue = {
    scrollToTop
  }

  return (
    <OverlayContext.Provider value={contextValue}>
      <OverlayContent
        ref={ref}
        className={className}
        shadeBackground={shadeBackground}
      >
        {children}
      </OverlayContent>
    </OverlayContext.Provider>
  )
}

export class Overlay extends React.PureComponent<PropsType, StateType> {
  static defaultProps = {
    transitionDuration: TRANSITION_DURATION,
    animationType: ANIMATION_TYPES.SLIDE_RIGHT,
    closeOnEsc: false,
    shadeBackground: true,
    preRenderContent: false,
    enableBodyScrollLock: true,
    enableContentBackgroundHack: false,
    allowTouchMove: false,
    isBackgroundClickEnabled: true
  }

  timeout: number | null | undefined

  constructor(props: PropsType) {
    super(props)

    this.state = {
      status: props.isOpen ? STATUSES.VISIBLE : STATUSES.INITIAL
    }
  }

  componentDidMount() {
    if (this.props.closeOnEsc) {
      document.addEventListener('keydown', this.handleKeyDown, false)
    }
  }

  componentWillUnmount() {
    const {closeOnEsc} = this.props

    if (closeOnEsc) {
      document.removeEventListener('keydown', this.handleKeyDown, false)
    }
  }

  // TODO: SyntheticKeyboardEvent<???>
  handleKeyDown = (e: any) => {
    const {onClose} = this.props

    const key = e.key || e.keyCode

    if (
      key === KEYSTRING_ESC ||
      key === KEYSTRING_SHORT_ESC ||
      key === KEYCODE_ESC
    ) {
      onClose && onClose()
    }
  }

  componentDidUpdate(prevProps: PropsType, {status}: StateType) {
    const {transitionDuration, isOpen} = this.props

    // @Todo split these functions out, so you have open and close.
    if (status === STATUSES.INITIAL && !prevProps.isOpen && isOpen) {
      this.timeout && window.clearInterval(this.timeout)

      this.setState({
        status: STATUSES.RENDERED
      })

      window.requestAnimationFrame(() => {
        this.setState({
          status: STATUSES.VISIBLE
        })
      })
    } else if (prevProps.isOpen && !isOpen) {
      this.timeout && window.clearInterval(this.timeout)

      this.setState({
        status: STATUSES.HIDDEN
      })

      this.timeout = window.setTimeout(() => {
        this.setState({
          status: STATUSES.INITIAL
        })
      }, transitionDuration)
    }
  }

  handleOverlayBackgroundClick: React.MouseEventHandler = e => {
    const {isBackgroundClickEnabled, onClose} = this.props

    if (!isBackgroundClickEnabled) return

    onClose && onClose(e, 'Shadow')
  }

  render() {
    const {
      animationType,
      customStyles,
      children,
      transitionDuration,
      containerStyles,
      shadeBackground,
      enableBodyScrollLock,
      enableContentBackgroundHack,
      allowTouchMove,
      preRenderContent
    } = this.props

    const {status} = this.state
    const transitionFunc =
      transitions[animationType] || transitions[ANIMATION_TYPES.SLIDE_RIGHT] // or default animation type if provided one is incorrect

    const transition = transitionFunc(transitionDuration)
    const backgroundTransition = transitions.overlayFade(transitionDuration)

    if (status === STATUSES.INITIAL && !preRenderContent) return null

    return (
      <Portal>
        <Transition
          in={status === STATUSES.VISIBLE}
          timeout={transitionDuration}
          classNames={transition}
        >
          {state => (
            <FullHeightContainer containerStyles={containerStyles}>
              {status !== STATUSES.INITIAL && (
                <OverlayBackground
                  css={backgroundTransition[state]}
                  shadeBackground={shadeBackground}
                  onClick={this.handleOverlayBackgroundClick}
                />
              )}
              {enableContentBackgroundHack && (
                <ContentBackgroundHack css={transition[state]} />
              )}
              <Content
                css={[customStyles, transition[state]]}
                shadeBackground={shadeBackground}
                enableBodyScrollLock={enableBodyScrollLock}
                allowTouchMove={allowTouchMove}
              >
                {children}
              </Content>
            </FullHeightContainer>
          )}
        </Transition>
      </Portal>
    )
  }
}

export default Overlay
